import React from "react";
import { nanoid } from "nanoid";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
} from "@mui/material";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import { Keyword } from "@quest_editor/mocks";

const KeywordField = ({
  keywordRows,
  setKeywordRows,
  emptyLabel,
}: {
  keywordRows: Keyword[];
  setKeywordRows: React.Dispatch<React.SetStateAction<Keyword[]>>;
  emptyLabel: string;
}) => {
  const { t } = useTranslation("questEditor");
  const [inputText, setInputText] = React.useState<string>("");

  const handleRemoveKeyword = (id: string) => {
    setKeywordRows((prev: Keyword[]) => prev.filter((row) => row.id !== id));
  };

  const handleAddKeywords = () => {
    if (!inputText.trim()) return;

    const keywords = inputText
      .split(/[,\r\n]+/)
      .map((k) => k.trim())
      .filter(Boolean);

    const newKeywords: Keyword[] = keywords.map((k) => ({
      id: nanoid(),
      value: k,
    }));

    setKeywordRows((prev: Keyword[]) => {
      const existingValues = new Set(prev.map((k) => k.value));
      const uniqueNew = newKeywords.filter(
        (k) => !existingValues.has(k.value),
      );
      return [...prev, ...uniqueNew];
    });
    setInputText("");
  };

  return (
    <Accordion
      sx={{
        ".MuiAccordionSummary-root .MuiSvgIcon-root": {
          transform: "rotate(0deg)",
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {keywordRows.length > 0 ? (
          <Typography color="primary">
            {keywordRows.map((k) => k.value).join(", ")}
          </Typography>
        ) : (
          <Typography color="primary">{emptyLabel}</Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" divider={<Divider />} spacing={1}>
          {keywordRows.map((row) => (
            <Stack
              key={row.id}
              direction="row"
              alignItems="center"
              spacing={4}
            >
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {row.value}
              </Typography>
              <IconButton onClick={() => handleRemoveKeyword(row.id)}>
                <ClearIcon />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder={t("common_field.keyword.placeholder")}
              value={inputText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setInputText(e.target.value)
              }
              multiline
              rows={3}
              sx={{ flexGrow: 1 }}
            />
            <SubActionButton
              onClick={handleAddKeywords}
            >
              {t("common_field.keyword.button")}
            </SubActionButton>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default KeywordField;
